import React from 'react';
import { Link, type LinkProps } from '@bbkShared/react-router';
import { cs } from '@bbkAdminUtils/css-utils';
import icons from '@bbkAdminUtils/icons';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { useGetProductLicense } from '@bbkAdminUtils/plans';

type Props = {
  text: React.ReactNode;
  classes?: string;
  iconNode?: React.ReactNode;
  trailingIconNode?: React.ReactNode;
  tooltip?: React.ReactNode;
  disabled?: boolean;
  active?: boolean;
  subMenu?: boolean;
  highlightBg?: boolean;
  expandChildren?: boolean;
  onClick?: React.MouseEventHandler;
  href?: string;
};
export const NavItem: React.FC<Props> = (props) => {
  const { text, classes, iconNode, trailingIconNode } = props;
  const { tooltip, disabled, active, expandChildren } = props;
  const { href, onClick } = props;
  const { highlightBg: propHighlightBg, subMenu } = props;

  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;

  // highlighting only top level menu
  // or if explicitly passed property
  const highlightBg =
    typeof propHighlightBg === 'boolean' ? propHighlightBg && active : active;

  const compiledClasses = cs(
    classes,
    'tw-flex tw-items-center tw-justify-between',
    'tw-pl-2.5 tw-pr-2.5 tw-py-[7px] tw-mb-1.5',
    'tw-rounded tw-cursor-pointer',
    'tw-font-medium tw-text-black/50 hover:tw-text-black/75',
    {
      'tw-text-[15px] tw-py-[5px] tw-mb-0 tw-text-black/60': subMenu,
      'tw-text-cbrneutral-300 tw-pointer-events-none': disabled,
      'tw-text-cbrneutral-800 tw-font-semibold': active,
      'tw-bg-white': !agsLicense && highlightBg,
      'tw-bg-cbrprimary-50': agsLicense && highlightBg,
    }
  );

  const children = (
    <>
      <div className="tw-flex tw-w-full tw-items-center">
        {iconNode && (
          <span className="tw-mr-2.5 tw-opacity-60 [&_svg]:tw-h-[22px] [&_svg]:tw-w-[22px]">
            {iconNode}
          </span>
        )}
        {text}
        {trailingIconNode && (
          <span className="tw-ml-2 tw-inline-block">{trailingIconNode}</span>
        )}
      </div>
      {typeof expandChildren === 'boolean' && (
        <span
          className={cs('bbk-nav-item-expander', {
            '[&_svg]:tw-rotate-180': expandChildren,
          })}
        >
          {icons.chevronDown}
        </span>
      )}
    </>
  );
  const absoluteLinksAttrs: Partial<LinkProps> = {
    target: '_blank',
    rel: 'noopener noreferrer',
  };
  const attrs =
    href && /^(https?|mailto):/.test(href) ? absoluteLinksAttrs : {};
  const navItem = href ? (
    <Link
      to={{ pathname: href }}
      {...attrs}
      className={compiledClasses}
      onClick={onClick}
    >
      {children}
    </Link>
  ) : (
    <div className={compiledClasses} onClick={onClick}>
      {children}
    </div>
  );

  const resultChildren = disabled ? (
    <span className={cs({ 'tw-cursor-not-allowed': disabled })}>{navItem}</span>
  ) : (
    navItem
  );

  const tooltipEl = tooltip ? (
    <TippyTooltip
      content={tooltip}
      tippyOptions={{
        placement: 'right-start',
        appendTo: () => document.body,
        zIndex: 10,
      }}
    >
      {resultChildren}
    </TippyTooltip>
  ) : (
    resultChildren
  );

  return tooltipEl;
};
